import { CircleAlert } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import MetricCard from '../../../components/common/MetricCard';
import Section from '../../../components/common/Section';
import type { Performance } from './model';
import { getShortMetricLabel } from './utils';

export interface PerformanceInfoProps {
    performance: Performance[];
    className?: string;
    accent?: 'muted' | 'purple' | 'blue' | 'green' | 'teal' | 'none';
}

const PerformanceInfo: React.FC<PerformanceInfoProps> = ({ performance, className, accent = 'muted' }) => {
    const { t } = useTranslation(['overallTraineeReport', 'traineeReports']);

    if (!performance.length) {
        return (
            <Section title={t('sections.performance.title')} className={className} accent={accent}>
                <div className="flex w-full justify-center rounded-xl p-8">
                    <div className="max-w-lg">
                        <div className="text-center">
                            <CircleAlert className="mx-auto h-12 w-12 text-gray-500" strokeWidth={2} />
                            <p className="mt-2 text-base font-semibold text-black">{t('empty.noDataTitle')}</p>
                            <p className="mt-1 max-w-[400px] text-base text-slate-500">
                                {t('empty.noDataDescription')}
                            </p>
                        </div>
                    </div>
                </div>
            </Section>
        );
    }

    return (
        <Section title={t('sections.performance.title')} className={className} accent={accent}>
            <div className="space-y-6">
                {performance.map(p => (
                    <div key={p.name} className="grid grid-cols-[120px_1fr] gap-4">
                        <div className="flex flex-col justify-center text-base">
                            <h4 className="leading-snug font-semibold text-gray-900">{p.name}</h4>
                            <p className="mt-1 text-gray-500">{p.date}</p>
                        </div>
                        <div className="flex w-full flex-wrap gap-3">
                            {p.result.map(m => (
                                <MetricCard
                                    key={m.label}
                                    label={getShortMetricLabel(t, m.label)}
                                    value={m.value as React.ReactNode}
                                    variant="plain"
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
};

export default PerformanceInfo;
